<template>
  <b-form class="survey-form">
    <b-container>
      <b-row class="border-bottom border-muted pb-3">
        <b-col lg="9" cols="9">
          <b-form-group label="Question" :label-for="`input-question-${index}`">
            <b-input
              :id="`input-question-${index}`"
              placeholder="Type your question here"
              required
              v-model="question.question"
              :maxlength="questionMaxLength"
              @keypress.native="filterAlphaNumericPunctuations"
              :readonly="isReadOnly"
              :state="isInvalid('question')"
            ></b-input>
            <b-form-invalid-feedback :state="isInvalid('question')">
              {{ errorMessage("question") }}
            </b-form-invalid-feedback>
            <slot name="image-render"></slot>
          </b-form-group>
        </b-col>
         <b-col lg="1" cols="1">
            <div :style="{ paddingBottom: '30px' }"></div> 
            <slot name="right-action-question-input"></slot>
        </b-col>
        <b-col v-if="isUcoin">
          <b-form-group label="Gigya Pairs">
            <b-input
              v-model="question.gigya_question_id"
              type="text"
            ></b-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col lg="3">
          <b-form-group label="Character length of answer">
            <b-form-input
              type="text"
              v-model="contents.char_length"
              placeholder="0"
              :disabled="isReadOnly"
              maxlength="3"
              @keypress.native="filterNumeric"
              :state="isInvalid('char_length')"
            >
            </b-form-input>
            <b-form-invalid-feedback :state="isInvalid('char_length')">
              {{ errorMessage("char_length") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col lg="4">
          <b-form-group label="Placeholder">
            <b-form-input
              type="text"
              v-model="contents.placeholder"
              placeholder="Type here"
              :disabled="isReadOnly"
              @keypress.native="filterAlphaNumericPunctuations"
              :maxlength="placeholderMaxLength"
              :state="isInvalid('placeholder')"
            >
            </b-form-input>
            <b-form-invalid-feedback :state="isInvalid('placeholder')">
              {{ errorMessage("placeholder") }}
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
        <b-col cols="6" lg="1">
          <b-form-group label="Visible" label-class="text-nowrap">
            <b-checkbox
              v-model="contents.visible"
              class="ml-3 mt-2"
              :disabled="isReadOnly"
            />
          </b-form-group>
        </b-col>
        <b-col cols="6" lg="1">
          <b-form-group label="Required" label-class="text-nowrap">
            <b-checkbox
              v-model="contents.required"
              class="ml-3 mt-2"
              :disabled="isReadOnly"
            />
          </b-form-group>
        </b-col>
        <b-col v-if="isUcoin">
          <b-form-group label="Gigya Pairs">
            <b-input
              type="text"
              v-model="contents.gigya_answer_id"
            ></b-input>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row class="mt-3 pt-3 border-top border-muted">
        <b-col lg="3" class="ml-auto">
          <div class="d-flex flex-row justify-content-end">
            <b-button
              variant="link"
              class="text-danger font-weight-bold mr-2"
              @click.self="showConfirmCancelQuestionModal"
              :disabled="isReadOnly"
            >
              Cancel
            </b-button>
            <b-button
              variant="primary btn-save"
              @click.self="validateFields"
              :disabled="isReadOnly"
            >
              Save
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-container>
  </b-form>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import notification from "@/services/notification";

export default {
    name: "ShortAnswerForm",
    props: ["pageId", "question", "contents", "index"],
    data() {
        return {
            questionMaxLength: 350,
            placeholderMaxLength: 50
        };
    },
    computed: {
        ...mapGetters("surveys", ["errors", "isReadOnly", "isUcoin"])
    },
    methods: {
        ...mapMutations("surveys", ["setQuestionProperties", "setErrors"]),
        isInvalid(field) {
            if (this.errors === null) {
                return null;
            } else {
                return !Object.keys(this.errors).includes(field)
                    ? null
                    : !Object.keys(this.errors).includes(field);
            }
        },
        errorMessage(field) {
            if (this.errors === null) {
                return "";
            } else if (Object.keys(this.errors).includes(field)) {
                return Array.isArray(this.errors[field])
                    ? this.errors[field].join(" ")
                    : this.errors[field];
            }
        },
        validateFields() {
            const errors = {};
            if (!this.question.question)
                errors["question"] = "Question is required";

            if (
                this.question.question &&
                this.isNotAlphaNumericPunctuations(this.question.question)
            ) {
                errors["question"] =
                    "Only alpha numeric with punctuation characters are allowed";
            }

            if (
                this.question.contents.placeholder &&
                this.isNotAlphaNumericPunctuations(
                    this.question.contents.placeholder
                )
            )
                errors["placeholder"] =
                    "Only alpha numeric with punctuation characters are allowed";
            if (!this.question.contents.char_length)
                errors["char_length"] = "Character length is required";
            else if (
                this.question.contents.char_length &&
                this.isNotNumeric(this.question.contents.char_length)
            )
                errors["char_length"] = "Only numeric characters are allowed";

            if (Object.keys(errors).length === 0)
                this.showConfirmSaveQuestionModal();
            else notification.warning("Oops, cannot save question.");

            this.setErrors(errors);
        },
        showConfirmSaveQuestionModal() {
            this.setQuestionProperties({
                type: "save",
                pageIndex: this.index.split("-")[0],
                questionIndex: this.index.split("-").pop(),
                question_type: this.question.question_type,
                page_uuid: this.pageId,
                question_uuid: this.question.uuid
            });
            this.$root.$emit(
                "bv::show::modal",
                "confirm-save-cancel-question-modal"
            );
        },
        showConfirmCancelQuestionModal() {
            this.setQuestionProperties({
                type: "cancel",
                pageIndex: this.index.split("-")[0],
                questionIndex: this.index.split("-").pop(),
                question_type: this.question.question_type,
                page_uuid: this.pageId,
                question_uuid: this.question.uuid
            });
            this.$root.$emit(
                "bv::show::modal",
                "confirm-save-cancel-question-modal"
            );
        },
        isNotAlphaNumericPunctuations(input) {
            return /[^A-Za-z0-9 ,.!?'":()\s\-]+/g.test(input);
        },
        filterAlphaNumericPunctuations(e) {
            if (
                this.isNotAlphaNumericPunctuations(
                    String.fromCharCode(e.keyCode)
                )
            )
                e.preventDefault();
        },
        isNotNumeric(input) {
            return /[^0-9]/g.test(input);
        },
        filterNumeric(e) {
            if (this.isNotNumeric(String.fromCharCode(e.keyCode)))
                e.preventDefault();
        }
    }
};
</script>
